import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import type { QuizQuestion } from '@/lib/quiz-parser';
import type { QuizInProgressSession } from '@/lib/quiz-in-progress-session';
import {
  clearQuizInProgressSession,
  saveQuizInProgressSession,
} from '@/lib/quiz-in-progress-session';
import {
  displayOptionsForOrder,
  type OptionOrder,
} from '@/lib/quiz-option-shuffle';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatQuizSessionScope,
  QuizSessionHeader,
} from './QuizSessionHeader';
import type { QuizSelection } from '@/lib/api/quiz';

interface QuizActiveModeProps {
  questions: QuizQuestion[];
  /** 0 = untimed (no countdown, no auto-submit on expiry). */
  timeLimitMinutes: number;
  slug: string;
  documentId: string;
  /** Restored draft from localStorage (same question order as `questions`). */
  initialSession?: QuizInProgressSession | null;
  /** Per-question option permutation (display a-d -> canonical keys). */
  optionOrders?: Record<string, OptionOrder>;
  /** Called with display-letter answers; parent converts to canonical before API. */
  onSubmit: (answers: Record<string, string>, startedAt: Date) => Promise<void>;
  submitting?: boolean;
  /** Parent reshuffles and clears draft. */
  onStartOver?: () => void;
  /** Abandon attempt and return to picker / start gate. */
  onExitSession?: () => void;
  /** Domain scope for session header. */
  selection?: QuizSelection | null;
  /** survey → Finish; exam → Submit quiz */
  submitLabel?: string;
}

const OPTION_KEYS = ['a', 'b', 'c', 'd'] as const;

function clampIndex(index: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(Math.max(0, index), total - 1);
}

/** Never steal keys while the user is typing in chat/composer/inputs. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"]',
    ),
  );
}

export function QuizActiveMode({
  questions,
  timeLimitMinutes,
  slug,
  documentId,
  initialSession = null,
  optionOrders = {},
  onSubmit,
  submitting = false,
  onStartOver,
  onExitSession,
  selection = null,
  submitLabel = 'Submit quiz',
}: QuizActiveModeProps) {
  const scopeLabel = formatQuizSessionScope(selection);
  const total = questions.length;
  const questionIds = questions.map((q) => q.id);
  const timed = timeLimitMinutes > 0;

  const [index, setIndex] = useState(() =>
    clampIndex(initialSession?.index ?? 0, total)
  );
  const [answers, setAnswers] = useState<Record<string, string>>(
    () => initialSession?.answers ?? {}
  );
  const [startedAt] = useState(
    () => (initialSession?.startedAt ? new Date(initialSession.startedAt) : new Date())
  );
  const [endsAt] = useState(() => {
    if (!timed) return null;
    if (initialSession?.endsAt) return new Date(initialSession.endsAt);
    return new Date(Date.now() + timeLimitMinutes * 60 * 1000);
  });
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!endsAt) return 0;
    return Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / 1000));
  });
  const [expired, setExpired] = useState(false);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const current = questions[index];
  const currentDisplayOptions = current
    ? optionOrders[current.id]
      ? displayOptionsForOrder(current.options || {}, optionOrders[current.id])
      : current.options || {}
    : {};

  const persist = useCallback(
    (next: { index?: number; answers?: Record<string, string> }) => {
      if (!slug || !documentId || questionIds.length === 0) return;
      const session: QuizInProgressSession = {
        v: 2,
        slug,
        documentId,
        questionIds,
        answers: next.answers ?? answersRef.current,
        optionOrders,
        index: next.index ?? index,
        startedAt: startedAt.toISOString(),
        endsAt: endsAt ? endsAt.toISOString() : startedAt.toISOString(),
        updatedAt: new Date().toISOString(),
      };
      saveQuizInProgressSession(session);
    },
    [slug, documentId, questionIds, index, startedAt, endsAt, optionOrders]
  );

  // Seed storage as soon as an attempt is live (so a flash remount can restore order)
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !slug || !documentId || questionIds.length === 0) return;
    seededRef.current = true;
    persist({});
  }, [slug, documentId, questionIds.length, persist]);

  const handleSubmitAll = useCallback(async () => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    clearQuizInProgressSession(slug, documentId);
    await onSubmit(answersRef.current, startedAt);
  }, [submitting, onSubmit, startedAt, slug, documentId]);

  useEffect(() => {
    if (!timed || !endsAt) return;
    if (secondsLeft <= 0) {
      if (!submittedRef.current) {
        setExpired(true);
        void handleSubmitAll();
      }
      return;
    }
    const t = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [timed, secondsLeft, endsAt, handleSubmitAll]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!current || submitting) return;
      // Quiz tab stays mounted while chat has focus - do not hijack keys while typing.
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) {
        return;
      }
      // Ignore modified shortcuts (Cmd/Ctrl/Alt) so browser/app shortcuts still work.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const availableKeys = OPTION_KEYS.filter((k) => !!currentDisplayOptions[k]);
      const selectedKey = answersRef.current[current.id];
      const selectedIdx = availableKeys.indexOf(
        selectedKey as (typeof OPTION_KEYS)[number],
      );

      const selectOption = (key: (typeof OPTION_KEYS)[number]) => {
        if (!currentDisplayOptions[key]) return;
        setAnswers((prev) => {
          const next = { ...prev, [current.id]: key };
          persist({ answers: next });
          return next;
        });
      };

      const goPrev = () => {
        if (index <= 0) return;
        const nextIndex = index - 1;
        setIndex(nextIndex);
        persist({ index: nextIndex });
      };

      const goNext = () => {
        if (index >= total - 1) return;
        const nextIndex = index + 1;
        setIndex(nextIndex);
        persist({ index: nextIndex });
      };

      const goNextOrSubmit = () => {
        if (index < total - 1) {
          goNext();
        } else {
          void handleSubmitAll();
        }
      };

      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 4) {
        e.preventDefault();
        const key = OPTION_KEYS[num - 1];
        selectOption(key);
        return;
      }

      // Left / Right (and h / l): previous / next question
      if (e.key === 'ArrowLeft' || e.key === 'h') {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'l') {
        e.preventDefault();
        // Right only advances; Enter/Space submits on the last question.
        goNext();
        return;
      }

      // Up / Down (and k / j): move selection among options
      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        if (availableKeys.length === 0) return;
        const nextIdx =
          selectedIdx <= 0 ? availableKeys.length - 1 : selectedIdx - 1;
        selectOption(availableKeys[nextIdx]);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        if (availableKeys.length === 0) return;
        const nextIdx =
          selectedIdx < 0 || selectedIdx >= availableKeys.length - 1
            ? 0
            : selectedIdx + 1;
        selectOption(availableKeys[nextIdx]);
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        // Space on a focused button should activate that button (native).
        if (
          e.key === ' ' &&
          e.target instanceof HTMLElement &&
          e.target.closest('button')
        ) {
          return;
        }
        e.preventDefault();
        goNextOrSubmit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, currentDisplayOptions, index, total, handleSubmitAll, submitting, persist]);

  if (!current) {
    return (
      <p className="text-sm text-muted-foreground p-6">No questions in this quiz.</p>
    );
  }

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const progress = ((index + 1) / total) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid={testIds.quizDocumentRenderer.activeMode}
      role="region"
      aria-label={`Question ${index + 1} of ${total}`}
    >
      {onExitSession ? (
        <QuizSessionHeader
          mode="quiz"
          scopeLabel={scopeLabel}
          onExitSession={onExitSession}
          disabled={submitting}
        />
      ) : null}
      <div className="px-5 py-3 border-b border-border space-y-2 shrink-0">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium tabular-nums">
            Q {index + 1} / {total}
            <span className="text-muted-foreground font-normal ml-2">
              ({answeredCount} answered)
            </span>
          </span>
          {timed ? (
            <span className={cn('tabular-nums', secondsLeft < 300 && 'text-red-500 font-medium')}>
              {mins}:{secs.toString().padStart(2, '0')} remaining
              {expired ? ' (time up)' : ''}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">No time limit</span>
          )}
        </div>
        <Progress value={progress} className="h-1.5" />
        <p className="text-[10px] text-muted-foreground/80" aria-live="polite">
          Keys: Left/Right previous/next - Up/Down options - 1-4 select - Enter continue
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">
          {current.domain.replace(/_/g, ' ')}
          {current.difficulty ? ` - ${current.difficulty}` : ''}
        </p>
        <h3 className="text-base font-medium leading-relaxed" id={`quiz-stem-${current.id}`}>
          {current.stem}
        </h3>
        <div
          className="space-y-2"
          role="radiogroup"
          aria-labelledby={`quiz-stem-${current.id}`}
        >
          {OPTION_KEYS.map((key, i) => {
            const selected = answers[current.id] === key;
            const label = currentDisplayOptions[key];
            if (!label) return null;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={submitting}
                onClick={() => {
                  setAnswers((prev) => {
                    const next = { ...prev, [current.id]: key };
                    persist({ answers: next });
                    return next;
                  });
                }}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-md border text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  selected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:bg-muted/50'
                )}
                data-testid={testIds.quizDocumentRenderer.optionButton(current.id, key)}
              >
                <span className="font-mono text-muted-foreground mr-2">{i + 1}.</span>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={index === 0 || submitting}
            aria-keyshortcuts="ArrowLeft"
            onClick={() => {
              const nextIndex = Math.max(0, index - 1);
              setIndex(nextIndex);
              persist({ index: nextIndex });
            }}
          >
            Previous
          </Button>
          {onStartOver ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={submitting}
              className="text-muted-foreground"
              data-testid={testIds.quizDocumentRenderer.startOver}
              onClick={() => {
                const reset = () => {
                  clearQuizInProgressSession(slug, documentId);
                  onStartOver();
                };
                if (answeredCount === 0) {
                  reset();
                  return;
                }
                toast.message('Start over?', {
                  description: 'Your in-progress answers on this device will be cleared.',
                  duration: 8000,
                  action: {
                    label: 'Start over',
                    onClick: reset,
                  },
                });
              }}
            >
              Start over
            </Button>
          ) : null}
        </div>
        {index < total - 1 ? (
          <Button
            size="sm"
            disabled={submitting}
            aria-keyshortcuts="ArrowRight Enter"
            onClick={() => {
              const nextIndex = index + 1;
              setIndex(nextIndex);
              persist({ index: nextIndex });
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={submitting}
            aria-keyshortcuts="Enter"
            onClick={() => void handleSubmitAll()}
            data-testid={testIds.quizDocumentRenderer.submitButton}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Submitting...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
